import { all, call, delay, put, select } from 'redux-saga/effects';
import * as R from 'remeda';
import { toastActions } from 'store/glue/toast';
import * as userSelectors from 'store/glue/user/selectors';
import { toSafeToasts } from 'store/toastSchema';
import { JwtFeature } from 'utility/jwt';

// Wraps a boot-time read operation with: tenantId + feature guards,
// bounded retry-with-backoff on transient failure, and toast-on-terminal-
// failure. Errors are never re-thrown so a single failing op cannot cancel
// the parallel `all([...])` that boots them.
//
// Retry lives here (not in sagaWrapper) because these ops are idempotent
// GETs; user-intent sagas wrapped by sagaWrapper often aren't.

const MAX_ATTEMPTS = 3;
const BACKOFFS_MS = [1000, 2000];

// errorWrapper throws Toast[] for structured server errors (deterministic —
// don't retry) and a single Toast for the fallback path (network/timeout —
// worth retrying). Anything else (raw Error) also gets retried.
function isTransient(error: unknown): boolean {
  return !Array.isArray(error);
}

export function* operationWrapper(
  work: (tenantId: string) => Generator<any, any, any>,
  featureGuard: JwtFeature | null,
) {
  // JWT-derived guards — stable across retries, so check once up front.
  const tenantId: ReturnType<typeof userSelectors.tenantId> = yield select(
    userSelectors.tenantId,
  );
  if (!R.isDefined(tenantId)) return;

  if (featureGuard !== null) {
    const hasFeature: ReturnType<typeof userSelectors.hasFeature> =
      yield select(userSelectors.hasFeature, { feature: featureGuard });
    if (!hasFeature) return;
  }

  // Retry loop. Transient misses back off and retry silently; permanent
  // errors break out immediately. Toast only fires after we give up.
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return (yield call(work, tenantId)) as unknown;
    } catch (error) {
      console.error(error);
      lastError = error;
      if (!isTransient(error)) break;
      if (attempt >= MAX_ATTEMPTS - 1) break;
      const backoff = BACKOFFS_MS[attempt];
      if (R.isDefined(backoff)) yield delay(backoff);
    }
  }

  const toasts = toSafeToasts(lastError);
  yield all(toasts.map((toast) => put(toastActions.addToast(toast))));
  return undefined;
}
